import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Form, Input, Button, Card, Typography, Divider, Alert, Checkbox, message } from 'antd';
import { UserOutlined, LockOutlined, MailOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';

const { Title, Text, Paragraph } = Typography;

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const onFinish = async (values) => {
    setLoading(true);
    setError('');

    // 确认密码已经在前端验证匹配，可以去除
    delete values.confirmPassword;
    delete values.agreement; // 移除agreement字段，后端不需要
    
    // 添加完整的调试输出
    console.log('注册表单数据:', values);
    console.log('浏览器当前URL:', window.location.href);
    
    try {
      // 直接调用fetch进行测试
      const response = await fetch('/api/auth/signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: values.username,
          password: values.password,
          email: values.email,
          fullName: values.fullName || null
        })
      });
      
      console.log('注册响应状态:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('注册错误响应:', errorText);
        throw new Error(errorText || '注册失败');
      }
      
      const data = await response.json();
      console.log('注册成功响应:', data);
      
      message.success('注册成功！请登录');
      navigate('/login', { state: { message: '注册成功！请登录您的账号' } });
    } catch (error) {
      console.error('注册错误:', error);
      setError('注册失败: ' + (error.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  };

  // 自定义密码校验规则
  const validatePassword = (_, value) => {
    if (value && value.length < 6) {
      return Promise.reject(new Error('密码长度必须至少为6个字符'));
    }
    return Promise.resolve();
  };

  // 确认密码的校验规则
  const validateConfirmPassword = ({ getFieldValue }) => ({
    validator(_, value) {
      if (!value || getFieldValue('password') === value) {
        return Promise.resolve();
      }
      return Promise.reject(new Error('两次输入的密码不匹配'));
    },
  });

  return (
    <div className="container" style={{ marginTop: '50px', maxWidth: '500px' }}>
      <Card style={{ boxShadow: '0 4px 8px rgba(0,0,0,0.1)' }}>
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <Title level={2}>注册账号</Title>
          <Text type="secondary">创建您的情绪记录应用账号</Text>
        </div>

        {error && (
          <Alert
            message={error}
            type="error"
            showIcon
            style={{ marginBottom: '24px' }}
            closable
            onClose={() => setError('')}
          />
        )}

        <Form
          name="register"
          onFinish={onFinish}
          layout="vertical"
          scrollToFirstError
        >
          <Form.Item
            name="username"
            label="用户名"
            rules={[
              { required: true, message: '请输入用户名!' },
              { min: 3, message: '用户名长度至少为3个字符' },
              { max: 50, message: '用户名长度不能超过50个字符' }
            ]}
          >
            <Input 
              prefix={<UserOutlined />} 
              placeholder="请输入用户名"
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="email"
            label="邮箱"
            rules={[
              { required: true, message: '请输入邮箱!' },
              { type: 'email', message: '请输入有效的邮箱地址!' }
            ]}
          >
            <Input 
              prefix={<MailOutlined />} 
              placeholder="请输入邮箱地址"
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="password"
            label="密码"
            rules={[
              { required: true, message: '请输入密码!' },
              { validator: validatePassword }
            ]}
            hasFeedback
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="请输入密码"
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="确认密码"
            dependencies={['password']}
            hasFeedback
            rules={[
              { required: true, message: '请确认密码!' },
              validateConfirmPassword
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="请再次输入密码"
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="fullName"
            label="姓名(可选)"
          >
            <Input 
              placeholder="请输入您的真实姓名(可选)"
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="agreement"
            valuePropName="checked"
            rules={[
              {
                validator: (_, value) =>
                  value ? Promise.resolve() : Promise.reject(new Error('请阅读并同意服务条款')),
              },
            ]}
          >
            <Checkbox>
              我已阅读并同意 <a href="#">服务条款</a> 和 <a href="#">隐私政策</a>
            </Checkbox>
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              loading={loading}
              block
            >
              注册
            </Button>
          </Form.Item>
        </Form>

        <Divider />

        <div style={{ textAlign: 'center' }}>
          <Text type="secondary">
            已有账号? <Link to="/login">立即登录</Link>
          </Text>
        </div>
      </Card>
    </div>
  );
};

export default RegisterPage; 